import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Plus, Trash2, Upload, Sparkles, Save, Eye } from "lucide-react";
import api from "@/lib/api";
import CreatorSidebar from "@/components/CreatorSidebar";
import Mashy from "@/components/Mashy";

export default function CreatorRouter() {
  return (
    <div className="min-h-screen bg-[#F7F9FC] flex" data-testid="creator-shell">
      <CreatorSidebar />
      <div className="flex-1 p-5 md:p-8 max-w-5xl">
        <Routes>
          <Route index element={<Navigate to="modules" replace />} />
          <Route path="modules" element={<Modules />} />
          <Route path="levels" element={<SimpleSection title="Learner Levels" hint="Beginner / Basic / Intermediate — tag modules by level." />} />
          <Route path="difficulty" element={<SimpleSection title="Difficulty Settings" hint="Easy / Medium / Hard." />} />
          <Route path="pacing" element={<SimpleSection title="Pacing Controls" hint="Slow / Standard / Fast." />} />
          <Route path="uploads" element={<Uploads />} />
          <Route path="words" element={<SimpleSection title="Word & Phrase Lists" hint="Upload CSV/JSON of vocabulary for a module." />} />
          <Route path="examples" element={<SimpleSection title="Examples Library" hint="Indian-context examples mapped to learner levels." />} />
          <Route path="mcqs" element={<SimpleSection title="CFU MCQs" hint="Check-for-understanding multiple choice questions per module." />} />
          <Route path="rubrics" element={<SimpleSection title="Feedback Rubrics" hint="How AI should mark and respond to answers." />} />
          <Route path="ai-tutor" element={<Behavior />} />
          <Route path="mashy" element={<MashyBehavior />} />
          <Route path="preview" element={<Preview />} />
        </Routes>
      </div>
    </div>
  );
}

function Header({ title, hint }) {
  return (
    <div className="mb-6">
      <div className="text-xs font-bold uppercase tracking-widest text-gray-500">Course Creator</div>
      <h1 className="font-display text-3xl">{title}</h1>
      {hint && <p className="text-gray-500 mt-1">{hint}</p>}
    </div>
  );
}

function SimpleSection({ title, hint }) {
  return (
    <div data-testid={`cc-section-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <Header title={title} hint={hint} />
      <div className="card-chunky p-6 flex items-center gap-4">
        <Mashy mood="happy" size={80} bounce={false} />
        <div>
          <div className="font-display text-lg">Section ready for content</div>
          <div className="text-sm text-gray-500">Drag in lists, CSVs, or use the module-level configuration tools.</div>
        </div>
      </div>
    </div>
  );
}

function Modules() {
  const [modules, setModules] = useState([]);
  const [form, setForm] = useState({ title: "", subject: "english", learner_level: "beginner", difficulty: "easy", pace: "slow", description: "" });

  const load = () => api.get("/course-creator/modules").then((r) => setModules(r.data.modules || []));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title) return;
    await api.post("/course-creator/modules", form);
    setForm({ ...form, title: "", description: "" });
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this module?")) return;
    await api.delete(`/course-creator/modules/${id}`);
    load();
  };

  return (
    <div data-testid="cc-modules">
      <Header title="Course Modules" hint="Tag modules by subject, level, difficulty, and pace." />

      <div className="card-chunky p-5 mb-5 grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Title">
          <input data-testid="cc-mod-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input" />
        </Field>
        <Field label="Subject">
          <Select value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} options={["english", "math"]} testId="cc-mod-subject" />
        </Field>
        <Field label="Learner Level">
          <Select value={form.learner_level} onChange={(v) => setForm({ ...form, learner_level: v })} options={["beginner", "basic", "intermediate"]} testId="cc-mod-level" />
        </Field>
        <Field label="Difficulty">
          <Select value={form.difficulty} onChange={(v) => setForm({ ...form, difficulty: v })} options={["easy", "medium", "hard"]} testId="cc-mod-difficulty" />
        </Field>
        <Field label="Pace">
          <Select value={form.pace} onChange={(v) => setForm({ ...form, pace: v })} options={["slow", "standard", "fast"]} testId="cc-mod-pace" />
        </Field>
        <Field label="Description" full>
          <textarea data-testid="cc-mod-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input min-h-[60px]" />
        </Field>
        <div className="md:col-span-2">
          <button onClick={create} data-testid="cc-create-module" className="btn-success">
            <Plus className="w-4 h-4" /> Create Module
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {modules.map((m) => (
          <div key={m.module_id} className="card-chunky p-4" data-testid={`cc-module-${m.module_id}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="font-display text-lg">{m.title}</div>
                <div className="text-xs text-gray-500 uppercase tracking-widest font-bold">{m.subject} • {m.learner_level} • {m.difficulty} • {m.pace}</div>
              </div>
              <button onClick={() => del(m.module_id)} data-testid={`cc-del-${m.module_id}`} className="text-gray-400 hover:text-[#FF4B4B]"><Trash2 className="w-4 h-4" /></button>
            </div>
            <p className="text-sm text-gray-600 mt-2">{m.description}</p>
          </div>
        ))}
        {modules.length === 0 && <div className="text-gray-500 text-sm">No modules yet — create your first above.</div>}
      </div>
    </div>
  );
}

function Uploads() {
  const [modules, setModules] = useState([]);
  const [moduleId, setModuleId] = useState("");
  const [artifacts, setArtifacts] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/course-creator/modules").then((r) => {
      setModules(r.data.modules || []);
      if (r.data.modules?.[0]) setModuleId(r.data.modules[0].module_id);
    });
  }, []);

  useEffect(() => {
    if (!moduleId) return;
    api.get(`/course-creator/modules/${moduleId}/artifacts`).then((r) => setArtifacts(r.data.artifacts || []));
  }, [moduleId]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !moduleId) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("module_id", moduleId);
    fd.append("artifact_type", "pdf");
    const r = await api.post("/course-creator/artifacts/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    await api.post("/course-creator/artifacts", {
      module_id: moduleId,
      artifact_type: "pdf",
      title: file.name,
      file_url: r.data.file_url,
    });
    const list = await api.get(`/course-creator/modules/${moduleId}/artifacts`);
    setArtifacts(list.data.artifacts || []);
    setUploading(false);
  };

  return (
    <div data-testid="cc-uploads">
      <Header title="Curriculum Uploads" hint="PDFs, worksheets, audio, images — stored to S3 (mocked in MVP1)." />
      <div className="card-chunky p-5 mb-5">
        <Field label="Target Module">
          <Select value={moduleId} onChange={setModuleId} options={modules.map((m) => ({ label: m.title, value: m.module_id }))} testId="cc-upload-module" />
        </Field>
        <label className="btn-primary mt-3 cursor-pointer inline-flex" data-testid="cc-upload-trigger">
          <Upload className="w-4 h-4" /> {uploading ? "Uploading..." : "Choose file"}
          <input type="file" onChange={upload} className="hidden" />
        </label>
        <p className="text-xs text-gray-500 mt-2">Bucket: <code>mashy-course-artifacts</code> (mock)</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {artifacts.map((a) => (
          <div key={a.artifact_id} className="card-chunky p-3 text-sm">
            <div className="font-bold">{a.title}</div>
            <div className="text-xs text-gray-500 break-all">{a.file_url}</div>
          </div>
        ))}
        {artifacts.length === 0 && <div className="text-sm text-gray-500">No artifacts yet.</div>}
      </div>
    </div>
  );
}

function Behavior() {
  const [modules, setModules] = useState([]);
  const [moduleId, setModuleId] = useState("");
  const [b, setB] = useState(null);

  useEffect(() => {
    api.get("/course-creator/modules").then((r) => {
      setModules(r.data.modules || []);
      if (r.data.modules?.[0]) setModuleId(r.data.modules[0].module_id);
    });
  }, []);
  useEffect(() => {
    if (!moduleId) return;
    api.get(`/course-creator/modules/${moduleId}/behavior`).then((r) => setB(r.data.behavior));
  }, [moduleId]);

  const save = async () => {
    if (!b || !moduleId) return;
    await api.put(`/course-creator/modules/${moduleId}/behavior`, b);
  };
  if (!b) return <SimpleSection title="AI Tutor Behaviour" hint="Loading..." />;

  return (
    <div data-testid="cc-behavior">
      <Header title="AI Tutor Behaviour" hint="Shape how Mashy explains, hints, and grades for this module." />
      <Field label="Module">
        <Select value={moduleId} onChange={setModuleId} options={modules.map((m) => ({ label: m.title, value: m.module_id }))} testId="cc-bh-module" />
      </Field>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <Field label="Explanation Language">
          <Select value={b.explanation_language} onChange={(v) => setB({ ...b, explanation_language: v })} options={["hindi", "english", "hinglish"]} testId="cc-bh-lang" />
        </Field>
        <Field label="Guidance Style">
          <Select value={b.guidance_style} onChange={(v) => setB({ ...b, guidance_style: v })} options={["direct", "step-by-step", "socratic", "mixed"]} testId="cc-bh-style" />
        </Field>
        <Field label="Hints Allowed">
          <input type="number" min={0} max={10} value={b.hints_allowed} onChange={(e) => setB({ ...b, hints_allowed: +e.target.value })} className="input" data-testid="cc-bh-hints" />
        </Field>
        <Field label="Practice Question Count">
          <input type="number" min={1} max={20} value={b.practice_questions_count} onChange={(e) => setB({ ...b, practice_questions_count: +e.target.value })} className="input" data-testid="cc-bh-pq" />
        </Field>
        <Field label="Retry Limit">
          <input type="number" min={0} max={10} value={b.retry_limit} onChange={(e) => setB({ ...b, retry_limit: +e.target.value })} className="input" data-testid="cc-bh-retry" />
        </Field>
        <Field label="AI Strictness">
          <Select value={b.ai_strictness} onChange={(v) => setB({ ...b, ai_strictness: v })} options={["gentle", "balanced", "exam-focused"]} testId="cc-bh-strictness" />
        </Field>
        <Field label="Mashy Frequency">
          <Select value={b.mascot_frequency} onChange={(v) => setB({ ...b, mascot_frequency: v })} options={["low", "medium", "high"]} testId="cc-bh-mascot" />
        </Field>
      </div>
      <button onClick={save} data-testid="cc-bh-save" className="btn-success mt-5"><Save className="w-4 h-4" /> Save Behaviour</button>
    </div>
  );
}

function MashyBehavior() {
  return (
    <div>
      <Header title="Mashy Behaviour" hint="Tweak mascot emotional reactions and frequency." />
      <div className="card-chunky p-6 flex items-center gap-4">
        <Mashy mood="celebrate" size={100} />
        <div>
          <div className="font-display text-lg flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#FFC800]" /> Default emotional states</div>
          <ul className="text-sm text-gray-600 mt-2 list-disc ml-5">
            <li>Jumps on correct answers</li>
            <li>Gentle shake (never red) on wrong answers</li>
            <li>Somersault on streak milestones</li>
            <li>Wag idle, looks confused on hints needed</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function Preview() {
  const nav = useNavigate();
  return (
    <div>
      <Header title="Preview as Learner" hint="See the student view of your module right now." />
      <div className="card-chunky p-6 flex items-center gap-4">
        <Mashy mood="happy" size={80} bounce={false} />
        <div className="flex-1">
          <div className="font-display text-lg">Open the learner dashboard</div>
          <div className="text-sm text-gray-500">Test all lessons, voice, tutor, and math screens with mock data.</div>
        </div>
        <button onClick={() => nav("/dashboard")} data-testid="cc-preview-open" className="btn-primary"><Eye className="w-4 h-4" /> Open</button>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`flex flex-col gap-1 ${full ? "md:col-span-2" : ""}`}>
      <span className="text-xs font-bold uppercase tracking-widest text-gray-500">{label}</span>
      {children}
    </label>
  );
}

function Select({ value, onChange, options, testId }) {
  const opts = options.map((o) => (typeof o === "string" ? { label: o, value: o } : o));
  return (
    <select data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)} className="input">
      {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}
